import { useState, useEffect, useRef } from "react";
import { Search, X, RefreshCw, ScrollText, Columns } from "lucide-react";

import { auditLog as auditLogApi } from "../lib/api.js";
import { useApi, useDebounce } from "../hooks/index.js";
import { Spinner, Empty, CloseX, Pagination } from "../components/ui.jsx";
import { useLang } from "../lib/i18n.jsx";

function fmtTime(date) {
  if (!date) return "—";
  return new Date(date).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const MODULES = ["RBAC", "SupportTeam", "AskGad"];

const ALL_COLUMNS = [
  { key: "dateTime",      labelKey: "auditLog.dateTime",      default: true },
  { key: "user",          labelKey: "tbl.user",               default: true },
  { key: "module",        labelKey: "auditLog.module",        default: true },
  { key: "action",        labelKey: "auditLog.action",        default: true },
  { key: "entityType",    labelKey: "auditLog.entityType",    default: false },
  { key: "entityId",      labelKey: "auditLog.entityId",      default: false },
  { key: "previousValue", labelKey: "auditLog.previousValue", default: false },
  { key: "newValue",      labelKey: "auditLog.newValue",      default: false },
  { key: "ipAddress",     labelKey: "auditLog.ipAddress",     default: true },
  { key: "browser",       labelKey: "auditLog.browser",       default: false },
  { key: "device",        labelKey: "auditLog.device",        default: false },
  { key: "sessionId",     labelKey: "auditLog.sessionId",     default: false },
  { key: "details",       labelKey: "auditLog.details",       default: true },
];

const STORAGE_KEY = "auditLogColumns";

function loadVisibleColumns() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (Array.isArray(stored) && stored.length) {
      return new Set(stored);
    }
  } catch {}
  return new Set(ALL_COLUMNS.filter(c => c.default).map(c => c.key));
}

function truncate(value, max = 60) {
  if (value == null) return "—";
  const str = typeof value === "string" ? value : JSON.stringify(value);
  return str.length > max ? str.slice(0, max) + "…" : str;
}

// ─── COLUMN MANAGER ───────────────────────────────────────────────────────────

function ColumnManager({ visible, onChange }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const toggle = (key) => {
    const next = new Set(visible);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange(next);
  };

  return (
    <div style={{ position: "relative" }} ref={ref}>
      <button className="btn btn-out btn-sm" onClick={() => setOpen(o => !o)}>
        <Columns size={12} /> {t("auditLog.manageColumns")}
      </button>
      {open && (
        <div style={{
          position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 20,
          background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8,
          padding: 10, minWidth: 200, boxShadow: "0 8px 24px rgba(0,0,0,.15)",
        }}>
          {ALL_COLUMNS.map(c => (
            <label key={c.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer", padding: "4px 2px" }}>
              <input type="checkbox" checked={visible.has(c.key)} onChange={() => toggle(c.key)} />
              {t(c.labelKey)}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── DETAILS MODAL ──────────────────────────────────────────────────────────

function DetailsModal({ log, onClose }) {
  const { t } = useLang();
  return (
    <div className="modal-ov open">
      <div className="modal">
        <div className="modal-ttl">{t("auditLog.details")} <CloseX onClick={onClose} /></div>
        <div className="f-grp">
          <div style={{ fontSize: 11, color: "var(--ghost)", marginBottom: 4 }}>{t("auditLog.previousValue")}</div>
          <pre style={{ background: "var(--surface)", borderRadius: 8, padding: 10, fontSize: 11, overflowX: "auto", margin: 0 }}>
            {log.previousValue ? JSON.stringify(log.previousValue, null, 2) : "—"}
          </pre>
        </div>
        <div className="f-grp">
          <div style={{ fontSize: 11, color: "var(--ghost)", marginBottom: 4 }}>{t("auditLog.newValue")}</div>
          <pre style={{ background: "var(--surface)", borderRadius: 8, padding: 10, fontSize: 11, overflowX: "auto", margin: 0 }}>
            {log.newValue ? JSON.stringify(log.newValue, null, 2) : "—"}
          </pre>
        </div>
        <button className="btn btn-out" style={{ width: "100%" }} onClick={onClose}>{t("common.close")}</button>
      </div>
    </div>
  );
}

export default function PageAuditLog({ toast }) {
  const { t } = useLang();
  const [search, setSearch] = useState("");
  const [module, setModule] = useState("");
  const [page, setPage] = useState(1);
  const [selLog, setSelLog] = useState(null);
  const [visibleCols, setVisibleCols] = useState(loadVisibleColumns);
  const limit = 30;

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...visibleCols]));
  }, [visibleCols]);

  const debSearch = useDebounce(search, 400);
  useEffect(() => { setPage(1); }, [debSearch, module]);

  const { data, loading, reload } = useApi(
    () => auditLogApi.list({ page, limit, ...(debSearch && { search: debSearch }), ...(module && { module }) }),
    [page, debSearch, module]
  );

  const logs = data?.logs ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const reset = () => { setSearch(""); setModule(""); setPage(1); };

  const columns = ALL_COLUMNS.filter(c => visibleCols.has(c.key));

  const renderCell = (l, key) => {
    switch (key) {
      case "dateTime":
        return <span style={{ fontFamily: "var(--fm)", fontSize: 11, color: "var(--ghost)" }}>{fmtTime(l.createdAt)}</span>;
      case "user":
        return l.userName
          ? <div><div style={{ fontWeight: 700, fontSize: 12 }}>{l.userName}</div><div style={{ fontSize: 10, color: "var(--ghost)" }}>{l.userEmail}</div></div>
          : <span style={{ color: "var(--ghost)", fontSize: 12 }}>{t("auditLog.system")}</span>;
      case "module":
        return <span className="badge bg-blue" style={{ fontSize: 9 }}>{l.module}</span>;
      case "action":
        return <span style={{ fontSize: 12, fontFamily: "var(--fm)" }}>{l.action}</span>;
      case "entityType":
        return <span style={{ fontSize: 12 }}>{l.entityType ?? "—"}</span>;
      case "entityId":
        return <span style={{ fontSize: 11, fontFamily: "var(--fm)", color: "var(--ghost)" }}>{l.entityId ?? "—"}</span>;
      case "previousValue":
        return <span style={{ fontSize: 11, fontFamily: "var(--fm)" }}>{truncate(l.previousValue)}</span>;
      case "newValue":
        return <span style={{ fontSize: 11, fontFamily: "var(--fm)" }}>{truncate(l.newValue)}</span>;
      case "ipAddress":
        return <span style={{ fontFamily: "var(--fm)", fontSize: 11, color: "var(--ghost)" }}>{l.ipAddress ?? "—"}</span>;
      case "browser":
        return <span style={{ fontSize: 12 }}>{l.browser ?? "—"}</span>;
      case "device":
        return <span style={{ fontSize: 12 }}>{l.device ?? "—"}</span>;
      case "sessionId":
        return <span title={t("auditLog.notAvailable")} style={{ fontSize: 12, color: "var(--ghost)" }}>—</span>;
      case "details":
        return (l.previousValue || l.newValue)
          ? <button className="btn btn-out btn-xs" onClick={() => setSelLog(l)}>{t("auditLog.details")}</button>
          : null;
      default:
        return null;
    }
  };

  return (
    <div>
      <div className="sec-hd">
        <div>
          <div className="sec-ttl"><ScrollText size={16} style={{ verticalAlign: "middle", marginRight: 6 }} />{t("auditLog.title")}</div>
          <div className="sec-sub">{t("auditLog.found", { n: total.toLocaleString() })}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <ColumnManager visible={visibleCols} onChange={setVisibleCols} />
          <button className="btn btn-out btn-sm" onClick={reload}><RefreshCw size={12} /></button>
        </div>
      </div>

      <div className="filter-bar">
        <div className="tb-search" style={{ maxWidth: 320, flex: 1 }}>
          <Search size={14} color="var(--ghost)" />
          <input
            placeholder={t("auditLog.searchPlaceholder")}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ghost)", display: "flex", alignItems: "center" }}>
              <X size={12} />
            </button>
          )}
        </div>
        <select data-testid="filter-module" className="f-in" style={{ width: "auto" }} value={module} onChange={e => setModule(e.target.value)}>
          <option value="">{t("auditLog.allModules")}</option>
          {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        {(search || module) && (
          <button className="btn btn-out btn-sm" onClick={reset}><RefreshCw size={12} /> {t("users.clear")}</button>
        )}
      </div>

      <div className="card">
        {loading ? <Spinner /> : logs.length === 0
          ? <div style={{ padding: 24 }}><Empty icon="📜" message={t("auditLog.noneFound")} /></div>
          : (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    {columns.map(c => <th key={c.key}>{t(c.labelKey)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {logs.map(l => (
                    <tr data-testid="audit-log-row" key={l.id}>
                      {columns.map(c => <td key={c.key}>{renderCell(l, c.key)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        <Pagination page={page} totalPages={totalPages} total={total} onPrev={() => setPage(p => p - 1)} onNext={() => setPage(p => p + 1)} />
      </div>

      {selLog && <DetailsModal log={selLog} onClose={() => setSelLog(null)} />}
    </div>
  );
}
